 
import React, { useState, useEffect } from 'react';
import { addBeneficiarytree, getBeneficiaryTree, updateBeneficiary } from '../services/authService';
import { showSuccess, showError } from '../utils/alert';

const AddBeneficiaryModal = ({ isOpen, onClose, onSave, householdId, villages, beneficiaryId }) => {
  const [formData, setFormData] = useState({
    village_id: '',
    name: '',
    gender: '',
    date_of_birth: '',
    contact_number: '',
    category: '',
    aadhar_number: '',
    household_id: householdId || '',
    active_status: true,
    relationship: '',
    is_primary_caregiver: false,
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Define relationship options based on the ENUM
  const relationshipOptions = [
    'Father', 'Mother', 'Son', 'Daughter',
    'Husband', 'Wife', 'Brother', 'Sister',
    'Grandfather', 'Grandmother', 'Grandson', 'Granddaughter',
    'Father-in-law', 'Mother-in-law', 'Son-in-law', 'Daughter-in-law',
    'Brother-in-law', 'Sister-in-law', 'Uncle', 'Aunt',
    'Nephew', 'Niece', 'Cousin', 'Guardian', 'Other'
  ];

  // Fetch beneficiary data when beneficiaryId is provided (edit mode)
  useEffect(() => {
    const fetchBeneficiary = async () => {
      if (beneficiaryId) {
        try {
          setFetching(true);
          const response = await getBeneficiaryTree(beneficiaryId); // GET request
          if (response.status === true && response.data) {
            const beneficiary = response.data;
            setFormData({
              village_id: beneficiary.village_id?.toString() || '',
              name: beneficiary.name || '',
              gender: beneficiary.gender || '',
              date_of_birth: beneficiary.date_of_birth ? beneficiary.date_of_birth.split('T')[0] : '',
              contact_number: beneficiary.contact_number || '',
              category: beneficiary.category || '',
              aadhar_number: beneficiary.aadhar_number || '',
              household_id: beneficiary.household_id || householdId || '',
              active_status: beneficiary.active_status ?? true,
              relationship: beneficiary.relationship || '',
              is_primary_caregiver: beneficiary.is_primary_caregiver ?? false,
              notes: beneficiary.notes || '',
            });
          } else {
            showError(response.message || 'Failed to fetch beneficiary data');
          }
        } catch (err) {
          showError('Error fetching beneficiary data');
        } finally {
          setFetching(false);
        }
      }
    };

    fetchBeneficiary();
  }, [beneficiaryId, householdId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!formData.name || !formData.village_id || !formData.gender || !formData.date_of_birth || !formData.category || !formData.household_id || !formData.relationship) {
      showError('Please fill all required fields, including relationship');
      return;
    }

    // Validate Aadhar number
    if (formData.aadhar_number && !/^\d{12}$/.test(formData.aadhar_number)) {
      showError('Aadhar number must be 12 digits');
      return;
    }

    // Validate contact number
    if (formData.contact_number && !/^\d{10}$/.test(formData.contact_number)) {
      showError('Contact number must be 10 digits');
      return;
    }

    // Validate category and gender consistency
    if (formData.category === 'Pregnant Woman' && formData.gender !== 'Female') {
      showError('Pregnant Woman category is only applicable for Female gender');
      return;
    }

    const payload = {
      village_id: parseInt(formData.village_id),
      name: formData.name,
      gender: formData.gender,
      date_of_birth: formData.date_of_birth,
      contact_number: formData.contact_number || null,
      category: formData.category,
      aadhar_number: formData.aadhar_number || null,
      household_id: formData.household_id,
      active_status: formData.active_status,
      relationship: formData.relationship,
      is_primary_caregiver: formData.is_primary_caregiver,
      notes: formData.notes || null,
    };

    try {
      setLoading(true);
      let response;

      if (beneficiaryId) {
        // Edit mode: Update existing beneficiary (PUT request)
        response = await updateBeneficiary(beneficiaryId, payload);
      } else {
        // Add mode: Create new beneficiary (POST request) 
        response = await addBeneficiarytree(payload);
      }

      if (response.status === true) {
        showSuccess(response.message || (beneficiaryId ? 'Beneficiary updated successfully' : 'Beneficiary added successfully'));
        // Reset form
        setFormData({
          village_id: '',
          name: '',
          gender: '',
          date_of_birth: '',
          contact_number: '',
          category: '',
          aadhar_number: '',
          household_id: householdId || '',
          active_status: true,
          relationship: '',
          is_primary_caregiver: false,
          notes: '',
        });
        onSave();
        onClose();
      } else {
        showError(response.message || 'Failed to save beneficiary');
      }
    } catch (err) {
      if (err.message.includes('duplicate key value violates unique constraint')) {
        showError('Aadhar number already exists. Please use a unique Aadhar number.');
      } else {
        showError('Network error or something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: #fff;
            padding: 20px;
            border-radius: 8px;
            width: 500px;
            max-height: 80vh;
            overflow-y: auto;
            position: relative;
          }
          .modal-close {
            position: absolute;
            top: 10px;
            right: 10px;
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
          }
          .modal-title {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 20px;
          }
          .form-section {
            margin-bottom: 20px;
          }
          .section-title {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 10px;
          }
          input[type="text"],
          input[type="date"],
          select,
          textarea {
            width: 100%;
            padding: 10px;
            border: 1px solid #ccc;
            border-radius: 4px;
            font-size: 16px;
          }
          textarea {
            min-height: 100px;
            resize: vertical;
          }
          .radio-group {
            display: flex;
            gap: 20px;
          }
          .radio * {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .checkbox-option {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .action-buttons {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
          }
          .cancel-button {
            background: #ccc;
            color: #333;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
          }
          .save-button {
            background: #1976d2;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
          }
          .cancel-button:hover {
            background: #bbb;
          }
          .save-button:hover {
            background: #1565c0;
          }
          .loading {
            opacity: 0.7;
            cursor: not-allowed;
          }
        `}
      </style>
      <div className="modal-overlay">
        <div className="modal-content">
          <button className="modal-close" onClick={onClose} disabled={loading || fetching}>×</button>
          <div className="modal-title">{beneficiaryId ? 'Edit Beneficiary' : 'Add New Beneficiary'}</div>

          {fetching && <div>Loading beneficiary data...</div>}

          {/* Name */}
          <div className="form-section">
            <div className="section-title">Name</div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter name"
              required
              disabled={loading || fetching}
            />
          </div>

          {/* Village Selection */}
          <div className="form-section">
            <div className="section-title">Village</div>
            <select
              name="village_id"
              value={formData.village_id}
              onChange={handleChange}
              required
              disabled={loading || fetching}
            >
              <option value="" disabled>Select village...</option>
              {villages.map((village) => (
                <option key={village.village_id} value={village.village_id}>
                  {village.village_name}
                </option>
              ))}
            </select>
          </div>

          {/* Gender */}
          <div className="form-section">
            <div className="section-title">Gender</div>
            <div className="radio-group">
              {['Male', 'Female', 'Other'].map((gender) => (
                <div className="radio-option" key={gender}>
                  <input
                    type="radio"
                    id={gender}
                    name="gender"
                    value={gender}
                    checked={formData.gender === gender}
                    onChange={handleChange}
                    required
                    disabled={loading || fetching}
                  />
                  <label htmlFor={gender}>{gender}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Date of Birth */}
          <div className="form-section">
            <div className="section-title">Date of Birth</div>
            <input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              required
              disabled={loading || fetching}
            />
          </div>

          {/* Contact Number */}
          <div className="form-section">
            <div className="section-title">Contact Number (Optional)</div>
            <input
              type="text"
              name="contact_number"
              value={formData.contact_number}
              onChange={handleChange}
              placeholder="Enter 10-digit contact number"
              disabled={loading || fetching}
            />
          </div>

          {/* Category */}
          <div className="form-section">
            <div className="section-title">Category</div>
            <div className="radio-group">
              {['Pregnant Woman', 'Child', 'Elderly', 'Other'].map((category) => (
                <div className="radio-option" key={category}>
                  <input
                    type="radio"
                    id={category}
                    name="category"
                    value={category}
                    checked={formData.category === category}
                    onChange={handleChange}
                    required
                    disabled={loading || fetching}
                  />
                  <label htmlFor={category}>{category}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Aadhar Number */}
          <div className="form-section">
            <div className="section-title">Aadhar Number (Optional)</div>
            <input
              type="text"
              name="aadhar_number"
              value={formData.aadhar_number}
              onChange={handleChange}
              placeholder="Enter 12-digit Aadhar number"
              disabled={loading || fetching}
            />
          </div>

          {/* Household ID */}
          <div className="form-section">
            <div className="section-title">Household ID</div>
            <input
              type="text"
              name="household_id"
              value={formData.household_id}
              onChange={handleChange}
              placeholder="Enter household ID"
              disabled
            />
          </div>

          {/* Relationship */}
          <div className="form-section">
            <div className="section-title">Relationship to Household Head</div>
            <select
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              required
              disabled={loading || fetching}
            >
              <option value="" disabled>Select relationship...</option>
              {relationshipOptions.map((rel) => (
                <option key={rel} value={rel}>
                  {rel}
                </option>
              ))}
            </select>
          </div>

          {/* Primary Caregiver */}
          <div className="form-section">
            <div className="section-title">Primary Caregiver</div>
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="is_primary_caregiver"
                name="is_primary_caregiver"
                checked={formData.is_primary_caregiver}
                onChange={handleChange}
                disabled={loading || fetching}
              />
              <label htmlFor="is_primary_caregiver">Is Primary Caregiver</label>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <div className="section-title">Notes (Optional)</div>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Enter any additional notes"
              disabled={loading || fetching}
            />
          </div>

          {/* Active Status */}
          <div className="form-section">
            <div className="section-title">Active Status</div>
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="active_status"
                name="active_status"
                checked={formData.active_status}
                onChange={handleChange}
                disabled={loading || fetching}
              />
              <label htmlFor="active_status">Active</label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={loading || fetching}
            >
              Cancel
            </button>
            <button
              className="save-button"
              onClick={handleSave}
              disabled={loading || fetching}
            >
              <i className="fas fa-save"></i> Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddBeneficiaryModal;
